import { useEffect, useRef } from 'react';

function attachTilt(el, max, scale) {
  let rafId = null;
  let rx = 0;
  let ry = 0;
  let s = 1;
  let trx = 0;
  let tryy = 0;
  let ts = 1;
  let hovering = false;

  const apply = () => {
    if (!hovering && Math.abs(rx) < 0.01 && Math.abs(ry) < 0.01 && Math.abs(s - 1) < 0.001) {
      el.style.transform = '';
    } else {
      el.style.transform = `perspective(900px) rotateX(${rx.toFixed(3)}deg) rotateY(${ry.toFixed(3)}deg) scale3d(${s.toFixed(4)}, ${s.toFixed(4)}, ${s.toFixed(4)})`;
    }
  };

  const step = () => {
    rx += (trx - rx) * 0.14;
    ry += (tryy - ry) * 0.14;
    s += (ts - s) * 0.16;
    apply();
    const settled = Math.abs(trx - rx) < 0.01 && Math.abs(tryy - ry) < 0.01 && Math.abs(ts - s) < 0.001;
    if (!settled) {
      rafId = requestAnimationFrame(step);
    } else {
      rx = trx;
      ry = tryy;
      s = ts;
      apply();
      rafId = null;
    }
  };

  const kick = () => {
    if (rafId === null) rafId = requestAnimationFrame(step);
  };

  const onMove = event => {
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const px = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const py = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
    // Bring the edge under the pointer toward the viewer.
    trx = (py - 0.5) * max * 2;
    tryy = (0.5 - px) * max * 2;
    ts = scale;
    hovering = true;
    kick();
  };

  const onLeave = () => {
    hovering = false;
    trx = 0;
    tryy = 0;
    ts = 1;
    kick();
  };

  el.style.willChange = 'transform';
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerleave', onLeave);

  return () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    el.removeEventListener('pointermove', onMove);
    el.removeEventListener('pointerleave', onLeave);
    el.style.transform = '';
    el.style.willChange = '';
  };
}

function useTilt({ max, scale }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const pointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    let cleanup = () => {};
    const setup = () => {
      cleanup();
      cleanup = () => {};
      if (!reduced.matches && pointer.matches) cleanup = attachTilt(el, max, scale);
    };
    setup();
    reduced.addEventListener('change', setup);
    pointer.addEventListener('change', setup);
    return () => {
      reduced.removeEventListener('change', setup);
      pointer.removeEventListener('change', setup);
      cleanup();
    };
  }, [max, scale]);
  return ref;
}

export default function Tilt({
  as: Tag = 'div',
  className = '',
  max = 8,
  scale = 1.02,
  style,
  children,
  ...rest
}) {
  const ref = useTilt({ max, scale });
  const classes = [className, 'tilt'].filter(Boolean).join(' ');
  return (
    <Tag ref={ref} className={classes} style={style} {...rest}>
      {children}
    </Tag>
  );
}
